import type { ErrorEvent } from '@sentry/nuxt'

// Header names that must never reach Sentry, matched case-insensitively.
const SENSITIVE_HEADERS = [
  'authorization',
  'cookie',
  'set-cookie',
  'x-api-key',
  'proxy-authorization',
]

// stripQueryString removes everything from the first "?" or "#" onwards. Invite
// links, password-reset links and OIDC callbacks carry single-use tokens in the
// query string, and those URLs show up in navigation/fetch breadcrumbs.
function stripQueryString(url: string): string {
  const cut = url.search(/[?#]/)
  return cut === -1 ? url : `${url.slice(0, cut)}?[scrubbed]`
}

// scrubSentryEvent strips sensitive data from an error event before it is sent:
// auth/cookie headers, token-bearing query strings (in the request AND in
// breadcrumb URLs), request bodies (which can carry credentials, customer free
// text, intake/audit content, or AI prompts), and the reporting user's IP.
// Defense in depth on top of sendDefaultPii: false.
export function scrubSentryEvent(event: ErrorEvent): ErrorEvent {
  if (event.request) {
    // Body and query string can contain credentials, customer content, prompts.
    delete event.request.data
    delete event.request.query_string
    delete event.request.cookies

    if (typeof event.request.url === 'string') {
      event.request.url = stripQueryString(event.request.url)
    }

    if (event.request.headers) {
      for (const key of Object.keys(event.request.headers)) {
        if (SENSITIVE_HEADERS.includes(key.toLowerCase())) {
          event.request.headers[key] = '[scrubbed]'
        }
      }
    }
  }

  // Breadcrumbs record every navigation and fetch, URLs included — a reset or
  // invite token would otherwise reach Sentry even with a clean request object.
  if (Array.isArray(event.breadcrumbs)) {
    for (const crumb of event.breadcrumbs) {
      const data = crumb?.data as Record<string, unknown> | undefined
      if (data && typeof data.url === 'string') {
        data.url = stripQueryString(data.url)
      }
      if (data && typeof data.to === 'string') {
        data.to = stripQueryString(data.to)
      }
      if (data && typeof data.from === 'string') {
        data.from = stripQueryString(data.from)
      }
    }
  }

  if (event.user) {
    delete event.user.ip_address
  }

  return event
}
